import lodash from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import URL from 'url-parse'
import PropTypes from 'prop-types'
import gqlAddToCart from '../graphql/addToCart'
import gqlCreatePayment from '../graphql/createPayment'
import gqlUpdatePayment from '../graphql/updatePayment'
import { useApolloClient } from '@apollo/client'
import getMayflyData from '../graphql/getMayflyData'
import trackCartEvent from '../utils/track-cart-event'
import { handleAsyncError, log } from '../utils/error-tracking'
import clientData from '../utils/client-data'
import React, {
  useState,
  createContext,
  useEffect,
  useRef,
  useCallback,
} from 'react'

export const CartContext = createContext({})

// Strip payment metadata fields before sending to mutations
const stripPaymentMetadata = (items) => {
  return items.map((item) => {
    // eslint-disable-next-line no-unused-vars
    const { ecToken: _ecToken, paymentId: _paymentId, timestamp: _timestamp, ...cartItemWithoutMetadata } = item
    return cartItemWithoutMetadata
  })
}

export const CartContextProvider = ({ children }) => {
  const client = useApolloClient()
  const [updateQueue, setUpdateQueue] = useState([])
  const [loading, setLoading] = useState(false)
  const [cartItems, setCartItems] = useState([])
  // ELMO: Check if user is in the AppClips eligibility experiment
  const isEcPrecomputeEnabled =
    clientData.experimentation?.appClipsEligibility === true
  const [ecToken, setEcToken] = useState(null)
  const [paymentId, setPaymentId] = useState(null)
  const [isFetchingToken, setIsFetchingToken] = useState(false)
  const [soldOutPromotionsBySku, setSoldOutPromotionsBySku] = useState([])
  const isFetchingRef = useRef(false)
  const previousCartItemsRef = useRef(null)
  const pendingCartItemsRef = useRef(null)
  const cartSyncInProgressRef = useRef(false)
  const shouldFetchTokenRef = useRef(false)

  async function fetchCartItems(cartId) {
    const res = await client.query({
      query: getMayflyData,
      variables: { mfKey: cartId },
      fetchPolicy: 'network-only',
    })

    return lodash.get(res, 'data.getMayflyData', [])
  }

  async function fetchAndUpdateCartItems(cartId) {
    const syncError = {
      file: 'cart-context',
      errMessagePrefix: 'Error when syncing cart',
    }

    await handleAsyncError(syncError, async () => {
      const fetchedCartItems = await fetchCartItems(cartId)

      // getMayflyData now returns array of items with payment metadata attached
      setCartItems(fetchedCartItems || [])

      // Load payment data from first item (they all have same payment metadata)
      const firstItem = fetchedCartItems && fetchedCartItems[0]
      if (firstItem) {
        if (firstItem.ecToken) {
          setEcToken(firstItem.ecToken)
        }
        if (firstItem.paymentId) {
          setPaymentId(firstItem.paymentId)
        }
      }

      setLoading(false)
    })
  }

  const syncCartItems = async (cartId, forceUpdate) => {
    if (!cartId) {
      localStorage.removeItem('cart_id')
      setCartItems([])
      setEcToken(null)
      setPaymentId(null)
      setLoading(false)
      return
    }

    if (!forceUpdate && localStorage.getItem('cart_id') === cartId) return

    localStorage.setItem('cart_id', cartId)
    fetchAndUpdateCartItems(cartId)
  }

  // Helper to check and process pending cart items
  const processPendingCartItems = useCallback((fetchFn) => {
    if (pendingCartItemsRef.current) {
      const pendingItems = pendingCartItemsRef.current
      pendingCartItemsRef.current = null
      fetchFn(pendingItems)
    }
  }, [])

  const fetchEcToken = useCallback(
    // eslint-disable-next-line max-statements
    async (items) => {
      // If already fetching, queue this request for later
      if (isFetchingRef.current) {
        pendingCartItemsRef.current = items
        return
      }

      isFetchingRef.current = true
      setIsFetchingToken(true)
      setEcToken(null)
      setSoldOutPromotionsBySku([])

      const cartInfo = { cartItems: stripPaymentMetadata(items) }

      try {
        let response
        let paymentData

        // Use updatePayment if paymentId exists, otherwise createPayment
        if (paymentId) {
          try {
            response = await client.mutate({
              mutation: gqlUpdatePayment,
              variables: {
                paymentId,
                cartInfo,
                mfKey: localStorage.getItem('cart_id'),
                ecToken,
              },
            })
            paymentData = lodash.get(response, 'data.updatePayment')
          } catch (patchError) {
            // PATCH failed - fallback to POST (create new payment)

            // Clear stale paymentId
            setPaymentId(null)

            // Retry with POST
            response = await client.mutate({
              mutation: gqlCreatePayment,
              variables: {
                cartInfo,
                stctype: new URL(window.location, true).query.stctype || '',
                sessionData: {
                  utm_source:
                    (sessionStorage && sessionStorage.getItem('utm_source')) ||
                    '',
                },
              },
            })
            paymentData = lodash.get(response, 'data.createPayment')
          }
        } else {
          response = await client.mutate({
            mutation: gqlCreatePayment,
            variables: {
              cartInfo,
              stctype: new URL(window.location, true).query.stctype || '',
              sessionData: {
                utm_source:
                  (sessionStorage && sessionStorage.getItem('utm_source')) ||
                  '',
              },
            },
          })
          paymentData = lodash.get(response, 'data.createPayment')
        }

        const promotionSoldOutErrors = lodash.get(
          paymentData,
          'promotionSoldOutErrors',
        )

        if (!lodash.isEmpty(promotionSoldOutErrors)) {
          setSoldOutPromotionsBySku(
            promotionSoldOutErrors.map((promoErr) => promoErr.sku),
          )
          setIsFetchingToken(false)
          isFetchingRef.current = false
          processPendingCartItems(fetchEcToken)
          return
        }

        const token = lodash.get(paymentData, 'ecToken')
        const newPaymentId = lodash.get(paymentData, 'paymentId')

        if (token) {
          setEcToken(token)
        }
        if (newPaymentId) {
          setPaymentId(newPaymentId)
        }

        setIsFetchingToken(false)
        isFetchingRef.current = false
        processPendingCartItems(fetchEcToken)
      } catch (err) {
        log({
          file: 'cart-context',
          err: { stack: 'Error fetching EC token', message: err.message },
        })
        setIsFetchingToken(false)
        isFetchingRef.current = false
        processPendingCartItems(fetchEcToken)
      }
    },
    [client, processPendingCartItems, paymentId, ecToken],
  )

  useEffect(() => {
    syncCartItems(localStorage.getItem('cart_id'), true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // EC-Token fetching after cart sync completes
  useEffect(() => {
    // ELMO: Skip precompute flow if user is NOT in experiment
    if (!isEcPrecomputeEnabled) {
      return undefined
    }

    // Don't fetch if cart is empty
    if (!cartItems || cartItems.length === 0) {
      // Only clear payment if we previously had items (user emptied the cart)
      // Don't clear on initial load when cart hasn't been fetched yet
      if (previousCartItemsRef.current !== null) {
        setEcToken(null)
        setPaymentId(null)
      }
      setSoldOutPromotionsBySku([])
      previousCartItemsRef.current = null
      shouldFetchTokenRef.current = false
      return undefined
    }

    // If this is initial page load and we already have ecToken, skip API call
    // Only fetch if cart actually changed (not just loaded from Mayfly)
    if (ecToken && previousCartItemsRef.current === null) {
      previousCartItemsRef.current = cartItems
      return undefined
    }

    // Deep comparison: only fetch if cart items actually changed
    const cartItemsChanged = !lodash.isEqual(
      previousCartItemsRef.current,
      cartItems,
    )

    if (!cartItemsChanged) {
      // Cart items are the same, no need to fetch
      return undefined
    }

    // Update the previous cart items reference
    previousCartItemsRef.current = cartItems

    // Check if we should fetch token now
    if (shouldFetchTokenRef.current && !cartSyncInProgressRef.current) {
      // Cart is synced and we're ready to fetch
      shouldFetchTokenRef.current = false
      fetchEcToken(cartItems)
    } else if (cartSyncInProgressRef.current) {
      // Cart sync in progress, mark to fetch after it completes
      shouldFetchTokenRef.current = true
      // Clear stale token - cart has changed
      setEcToken(null)
    } else if (updateQueue.length === 0 && !cartSyncInProgressRef.current) {
      // Queue operations (remove/update) - cart is already synced
      fetchEcToken(cartItems)
    } else {
      // Queue operations in progress
      shouldFetchTokenRef.current = true
      // Clear stale token - cart has changed
      setEcToken(null)
    }

    return undefined
  }, [cartItems, fetchEcToken, updateQueue.length, isEcPrecomputeEnabled, ecToken])

  useEffect(() => {
    if (updateQueue.length === 0) {
      // Queue is empty - cart sync operations completed
      cartSyncInProgressRef.current = false
      return
    }

    // Queue has items, mark sync as in progress
    cartSyncInProgressRef.current = true

    const lastUpdate = updateQueue[updateQueue.length - 1]

    // check if last update has resolved
    if (lastUpdate.resolvedCartId) {
      syncCartItems(lastUpdate.resolvedCartId)
      // Queue operation completed, clear sync flag and fetch token
      cartSyncInProgressRef.current = false
      if (shouldFetchTokenRef.current && cartItems && cartItems.length > 0 && isEcPrecomputeEnabled) {
        shouldFetchTokenRef.current = false
        fetchEcToken(cartItems)
      }
      return
    }

    const processUpdateQueue = async (_updateQueue) => {
      const resolvedUpdateQueue = await Promise.all(
        _updateQueue.map(async (update) => ({
          ...update,
          resolvedCartId: await update.promise,
        })),
      )

      setUpdateQueue((prevQueue) =>
        prevQueue.map((prevUpdate) => {
          const foundResolved = resolvedUpdateQueue.find(
            (resolved) => resolved.id === prevUpdate.id,
          )
          if (foundResolved) return foundResolved
          return prevUpdate
        }),
      )
    }

    processUpdateQueue([...updateQueue])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateQueue])

  const generateCartItems = ({
    existingCartItems = [],
    cartItemToUpdate,
    updatedCartItem,
  }) => {
    const updatedCartItems = []
    let hasUpdatedItem = false // used to short circuit update to only a single item

    for (const item of existingCartItems) {
      if (!hasUpdatedItem && lodash.isEqual(item, cartItemToUpdate)) {
        if (updatedCartItem) updatedCartItems.push(updatedCartItem)
        hasUpdatedItem = true
        continue
      }

      updatedCartItems.push(item)
    }

    return updatedCartItems
  }

  const _updateCart = async ({
    source = '_updateCart',
    updatedCartItems = [],
    newCartItems = [],
    queueAsync = true,
    // eslint-disable-next-line consistent-return
  }) => {
    // Mark that cart sync is in progress BEFORE updating cart items
    cartSyncInProgressRef.current = true

    setCartItems([...updatedCartItems, ...newCartItems])

    const promise = handleAsyncError(
      {
        file: 'cart-context',
        errMessagePrefix: `Graphql error on ${source}`,
      },
      async () => {
        const response = await client.mutate({
          mutation: gqlAddToCart,
          variables: {
            cartInfo: {
              cartItems: stripPaymentMetadata(updatedCartItems),
              newItems: stripPaymentMetadata(newCartItems),
            },
            ecToken,
          },
        })

        const newCartId = lodash.get(response, 'data.addToCart.cart_id')
        return newCartId
      },
    )

    if (queueAsync) {
      setUpdateQueue((prevUpdateQueue) => [
        ...prevUpdateQueue,
        {
          id: uuidv4(),
          promise,
        },
      ])
    }

    if (!queueAsync) {
      const result = await promise
      // Mark sync as complete for addToCart
      cartSyncInProgressRef.current = false
      // Trigger EC-token fetch now that cart is synced
      if (updatedCartItems.length > 0 || newCartItems.length > 0) {
        shouldFetchTokenRef.current = true
      }
      return syncCartItems(result)
    }
  }

  const addToCart = (newItems) => {
    setLoading(true)
    trackCartEvent('setCart', [...cartItems, ...newItems])

    return _updateCart({
      source: 'addToCart',
      updatedCartItems: cartItems,
      newCartItems: newItems,
      queueAsync: false,
    })
  }

  const removeCartItem = (itemToRemove) => {
    const filteredCartItems = generateCartItems({
      existingCartItems: cartItems,
      cartItemToUpdate: itemToRemove,
    })

    trackCartEvent('removeFromCart', cartItems, itemToRemove)

    _updateCart({
      source: 'removeCartItem',
      updatedCartItems: filteredCartItems,
    })
  }

  const updateCartItem = (itemToUpdate, updatedItem) => {
    const updatedCartItems = generateCartItems({
      existingCartItems: cartItems,
      cartItemToUpdate: itemToUpdate,
      updatedCartItem: updatedItem,
    })

    _updateCart({
      source: 'removeCartItem',
      updatedCartItems,
    })
  }

  const resetCartItems = () => syncCartItems(null)

  return (
    <CartContext.Provider
      value={{
        loading,
        cartItems,
        addToCart,
        removeCartItem,
        updateCartItem,
        resetCartItems,
        ecToken,
        isFetchingToken,
        soldOutPromotionsBySku,
        isEcPrecomputeEnabled,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

CartContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
